import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { formatVehiclePublicLabel, type Trip } from '@ubus/shared';

export type DetailPhase = 'not_today' | 'not_yet' | 'ready' | 'in_progress' | 'readonly';

export function getDetailPhase(trip: Trip): DetailPhase {
  if (trip.status === 'completed' || trip.status === 'cancelled') return 'readonly';
  if (trip.status === 'in_progress') return 'in_progress';
  const today = dayjs().format('YYYY-MM-DD');
  if (trip.date !== today) return 'not_today';
  const pickup = dayjs(`${trip.date} ${formatPickupTime(trip.pickup_time)}`, 'YYYY-MM-DD HH:mm');
  if (pickup.isAfter(dayjs())) return 'not_yet';
  if (trip.status === 'dispatched' || trip.status === 'scheduled') return 'ready';
  return 'readonly';
}

function formatPickupTime(pickupTime: string): string {
  const direct = dayjs(pickupTime, ['HH:mm:ss', 'HH:mm'], true);
  if (direct.isValid()) return direct.format('HH:mm');
  const iso = dayjs(`1970-01-01T${pickupTime}`);
  if (iso.isValid()) return iso.format('HH:mm');
  return pickupTime?.slice(0, 5) || '--:--';
}

export function formatDepartureDateTime(trip: Trip): string {
  return `${trip.date} ${formatPickupTime(trip.pickup_time)}`;
}

/** First segment before comma — used as place title in Lanhu cards. */
export function extractPlaceName(address: string, t?: TFunction): string {
  const trimmed = address.trim();
  if (!trimmed) return t ? t('task.unknownLocation') : 'Unknown location';
  const first = trimmed.split(',')[0]?.trim();
  return first || trimmed;
}

export function formatTripVehicleLabel(trip: Trip, t?: TFunction): string {
  const vehicleRow = Array.isArray(trip.vehicles) ? trip.vehicles[0] : trip.vehicles;
  if (!vehicleRow) return t ? t('task.noVehicle') : 'No vehicle assigned';
  return formatVehiclePublicLabel(vehicleRow);
}

export function formatPassengerCapacity(trip: Trip): string {
  const capacity = trip.vehicles?.capacity;
  if (capacity) return `${trip.passenger_count}/${capacity}`;
  return String(trip.passenger_count);
}

export function formatEstimatedArrival(trip: Trip): string {
  if (trip.estimated_dropoff_time) {
    return formatPickupTime(trip.estimated_dropoff_time);
  }
  const pickup = dayjs(`${trip.date} ${formatPickupTime(trip.pickup_time)}`, 'YYYY-MM-DD HH:mm');
  const fallbackMin =
    typeof trip.route_drive_minutes === 'number' && trip.route_drive_minutes > 0
      ? trip.route_drive_minutes
      : 25;
  return pickup.add(fallbackMin, 'minute').format('HH:mm');
}

/**
 * Task list / detail headline time:
 * - not started → departure (pickup) only
 * - in progress → estimated delivery / drop-off
 */
export function formatTaskScheduleHeadline(trip: Trip, t?: TFunction): string {
  if (trip.status === 'in_progress') {
    const time = formatEstimatedArrival(trip);
    return t ? t('task.expectedArrival', { time }) : time;
  }
  const time = formatDepartureDateTime(trip);
  return t ? t('task.departureTimeLine', { time }) : time;
}

export function formatPickupAndDropoffLine(trip: Trip, t?: TFunction): string {
  const pickup = formatPickupTime(trip.pickup_time);
  const dropoff = trip.estimated_dropoff_time
    ? formatPickupTime(trip.estimated_dropoff_time)
    : null;
  const mins =
    typeof trip.route_drive_minutes === 'number' && trip.route_drive_minutes > 0
      ? trip.route_drive_minutes
      : null;
  if (t) {
    if (dropoff && mins != null) {
      return t('task.pickupDropoffMinsLine', { pickup, dropoff, mins });
    }
    if (dropoff) return t('task.pickupDropoffLine', { pickup, dropoff });
    return t('task.pickupLine', { pickup });
  }
  if (dropoff && mins != null) {
    return `Pickup ${pickup} · Dropoff ${dropoff} · ~${mins} min`;
  }
  if (dropoff) return `Pickup ${pickup} · Dropoff ${dropoff}`;
  return `Pickup ${pickup}`;
}

export function formatHistoryHeadline(trip: Trip, t?: TFunction): string {
  const stamp = trip.updated_at
    ? dayjs(trip.updated_at).format('YYYY-MM-DD HH:mm')
    : formatDepartureDateTime(trip);
  if (t) {
    return trip.status === 'cancelled'
      ? t('task.cancellationHeadline', { stamp })
      : t('task.deliveryHeadline', { stamp });
  }
  return trip.status === 'cancelled' ? `Cancellation: ${stamp}` : `Delivery: ${stamp}`;
}

export type DateRange = 'today' | 'tomorrow' | 'future';

export function getDateFilter(range: DateRange): { gte: string; lte: string | null } {
  const today = dayjs().format('YYYY-MM-DD');
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  if (range === 'today') return { gte: today, lte: today };
  if (range === 'tomorrow') return { gte: tomorrow, lte: tomorrow };
  return { gte: dayjs().add(2, 'day').format('YYYY-MM-DD'), lte: null };
}

export function tripMatchesRange(trip: Trip, range: DateRange): boolean {
  const { gte, lte } = getDateFilter(range);
  if (trip.date < gte) return false;
  if (lte && trip.date > lte) return false;
  return true;
}
